import React, { useState, useRef } from "react";
import { Box, Stack, Typography, IconButton, Popover } from "@mui/material";
import { motion } from "framer-motion";
import { FiChevronLeft, FiChevronRight, FiChevronDown } from "react-icons/fi";
import { T } from "../../constants/todoTokens";
import MiniCalendar from "./MiniCalendar";

const VIEWS = [

  { key: "day",      label: "Day"      },
  { key: "week",     label: "Week"     },
  { key: "month",    label: "Month"    },
  { key: "schedule", label: "Schedule" },
];

const CalendarHeader = ({
  headerTitle,
  periodTaskCount,
  calView,
  isCurrentPeriodNow,
  onPrev,
  onNext,
  onToday,
  onViewChange,
  // mini-calendar props
  activeDate,
  taskMap,
  onSelectDay,
}) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const titleRef = useRef(null);

  const handleSelectDay = (date) => {
    onSelectDay(date);
    setPickerOpen(false);
  };

  return (
    <>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent="space-between"
        gap={1}
        sx={{ px: { xs: 2, sm: 2.5 }, py: { xs: 1.5, sm: 1.5 }, borderBottom: `1px solid ${T.border}` }}
      >
        {/* ── Left: clickable title (opens mini calendar) + Today ── */}
        <Stack direction="row" alignItems="center" gap={1.5}>
          <Box
            ref={titleRef}
            onClick={() => setPickerOpen((v) => !v)}
            sx={{
              display: "flex", alignItems: "center", gap: "6px",
              cursor: "pointer",
              borderRadius: "8px",
              px: 1, py: 0.5, ml: -1,
              transition: "background 0.15s",
              "&:hover": { bgcolor: T.primaryLight },
            }}
          >
            <Box>
              <Typography
                sx={{ fontSize: { xs: "15px", sm: "17px" }, fontWeight: 800, color: T.text, lineHeight: 1.1 }}
              >
                {headerTitle}
              </Typography>
              {periodTaskCount > 0 && (
                <Typography sx={{ fontSize: "11px", color: T.textMuted, fontWeight: 500, mt: "2px" }}>
                  {periodTaskCount} task{periodTaskCount !== 1 ? "s" : ""}
                </Typography>
              )}
            </Box>
            <motion.div
              animate={{ rotate: pickerOpen ? 180 : 0 }}
              transition={{ duration: 0.18 }}
              style={{ display: "flex", color: pickerOpen ? T.primary : T.textMuted }}
            >
              <FiChevronDown size={16} />
            </motion.div>
          </Box>

          {!isCurrentPeriodNow && (
            <motion.button
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.96 }}
              onClick={onToday}
              style={{
                padding: "4px 12px",
                border: `1.5px solid ${T.border}`,
                borderRadius: 7,
                background: "#fff",
                cursor: "pointer",
                fontFamily: "inherit",
                fontSize: "11px",
                fontWeight: 700,
                color: T.textSub,
              }}
            >
              Today
            </motion.button>
          )}
        </Stack>

        {/* ── Right: view toggle + nav arrows ── */}
        <Stack direction="row" alignItems="center" gap={1}>
          {/* Day / Week / Month / Schedule toggle */}
          <Box
            sx={{
              display: "inline-flex",
              bgcolor: T.bg,
              borderRadius: "9px",
              p: "3px",
              border: `1px solid ${T.border}`,
            }}
          >
            {VIEWS.map(({ key, label }) => (
              <Box key={key} sx={{ position: "relative" }}>
                {calView === key && (
                  <motion.div
                    layoutId="calViewPill"
                    style={{
                      position: "absolute", inset: 0,
                      background: T.surface,
                      borderRadius: "6px",
                      boxShadow: T.shadow.sm,
                    }}
                    transition={{ type: "spring", stiffness: 500, damping: 35 }}
                  />
                )}
                <Box
                  component="button"
                  onClick={() => {
                    onViewChange(key);
                    if (key === "schedule") setPickerOpen(true);
                  }}
                  sx={{
                    position: "relative", zIndex: 1,
                    display: "flex", alignItems: "center",
                    px: { xs: "8px", sm: "11px" }, py: "6px",
                    border: "none", background: "transparent",
                    cursor: "pointer", borderRadius: "6px",
                    color: calView === key ? T.primary : T.textMuted,
                    fontWeight: calView === key ? 700 : 500,
                    fontSize: { xs: "10px", sm: "11px" },
                    fontFamily: "inherit",
                    transition: "color 0.15s",
                    "&:hover": { color: T.text },
                  }}
                >
                  {label}
                </Box>
              </Box>
            ))}
          </Box>

          {/* Prev / Next arrows — hidden in Schedule view (date chosen via picker) */}
          {calView !== "schedule" && (
            <Stack direction="row" gap={0}>
              <IconButton
                onClick={onPrev}
                size="small"
                sx={{ color: T.textSub, "&:hover": { bgcolor: T.primaryLight, color: T.primary } }}
              >
                <FiChevronLeft size={17} />
              </IconButton>
              <IconButton
                onClick={onNext}
                size="small"
                sx={{ color: T.textSub, "&:hover": { bgcolor: T.primaryLight, color: T.primary } }}
              >
                <FiChevronRight size={17} />
              </IconButton>
            </Stack>
          )}
        </Stack>
      </Stack>

      {/* ── Mini-calendar date-picker popover ── */}
      <Popover
        open={pickerOpen}
        anchorEl={titleRef.current}
        onClose={() => setPickerOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top",    horizontal: "left" }}
        PaperProps={{
          elevation: 0,
          sx: {
            mt: 1,
            borderRadius: "16px",
            border: `1px solid ${T.border}`,
            boxShadow: T.shadow.lg,
            overflow: "hidden",
            width: 260,
          },
        }}
      >
        <MiniCalendar
          activeDate={activeDate}
          calView={calView}
          taskMap={taskMap}
          onSelectDay={handleSelectDay}
        />
      </Popover>
    </>
  );
};

export default React.memo(CalendarHeader);
